import {
  User,
  ExternalLink,
  UserCog,
  ShieldCheck,
  CreditCard,
  CalendarPlus,
  Building2,
  LockOpen,
} from 'lucide-react'
import { toAbsoluteUrl } from './utils'
import type { UserActions, CompanyActions } from './types'

const USER_ACTION_CONFIG = {
  impersonate: { label: 'Acessar como', icon: UserCog },
  admin: { label: 'Admin', icon: ShieldCheck },
} as const

const COMPANY_ACTION_CONFIG = {
  impersonate_owner: { label: 'Acessar Responsável', icon: UserCog },
  set_pending_payment: { label: 'Pgto. Pendente', icon: CreditCard },
  increase_trial_5_days: { label: 'Trial +5d', icon: CalendarPlus },
  increase_trial_7_days: { label: 'Trial +7d', icon: CalendarPlus },
  remove_read_only: { label: 'Remover Read Only', icon: LockOpen },
} as const

interface AdminActionsPanelProps {
  userActions?: UserActions
  companyActions?: CompanyActions
  borderless?: boolean
}

export default function AdminActionsPanel({
  userActions,
  companyActions,
  borderless = false,
}: AdminActionsPanelProps) {
  const hasUserActions = userActions && Object.values(userActions).some(Boolean)
  const hasCompanyActions = companyActions && Object.values(companyActions).some(Boolean)

  if (!hasUserActions && !hasCompanyActions) return null

  return (
    <div className={borderless ? '' : 'border-b border-gray-200'}>
      {hasUserActions && (
        <div className={borderless ? 'pb-3' : 'p-4 pb-3'}>
          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2.5 flex items-center gap-1.5">
            <User className="w-3.5 h-3.5" />
            Ações do Usuário
          </h4>
          <div className="flex flex-wrap gap-1.5">
            {Object.entries(userActions).map(([key, url]) => {
              if (!url) return null
              const config = USER_ACTION_CONFIG[key as keyof typeof USER_ACTION_CONFIG]
              if (!config) return null
              const IconComponent = config.icon
              return (
                <a
                  key={key}
                  href={toAbsoluteUrl(url)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-md border border-gray-200 text-purple-600 hover:bg-purple-50 transition-colors"
                >
                  <IconComponent className="w-3.5 h-3.5" />
                  {config.label}
                  <ExternalLink className="w-3 h-3 opacity-50" />
                </a>
              )
            })}
          </div>
        </div>
      )}
      {hasCompanyActions && (
        <div className={borderless ? 'pt-0' : 'p-4 pt-0'}>
          {hasUserActions && <div className="border-t border-gray-100 pt-3 -mt-1" />}
          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2.5 flex items-center gap-1.5">
            <Building2 className="w-3.5 h-3.5" />
            Ações da Empresa
          </h4>
          <div className="flex flex-wrap gap-1.5">
            {Object.entries(companyActions).map(([key, url]) => {
              if (!url) return null
              const config = COMPANY_ACTION_CONFIG[key as keyof typeof COMPANY_ACTION_CONFIG]
              if (!config) return null
              const IconComponent = config.icon
              return (
                <a
                  key={key}
                  href={toAbsoluteUrl(url)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-md border border-gray-200 text-blue-600 hover:bg-blue-50 transition-colors"
                >
                  <IconComponent className="w-3.5 h-3.5" />
                  {config.label}
                  <ExternalLink className="w-3 h-3 opacity-50" />
                </a>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
